import React, { useState, useEffect, useCallback } from 'react';
import Layout from '../../components/Layout';
import api from '../../utils/api';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { toast } from 'sonner';
import { CalendarRange, Upload, Trash2, CheckCircle, Loader2, AlertTriangle, FileText } from 'lucide-react';

const BAND_OPTIONS = ['A', 'B', 'C', 'D', 'E', 'F'];
const fmtDate = (iso) => { const [y, m, d] = iso.split('-'); return `${d}/${m}/${y}`; };

const BandChip = ({ color, band }) => (
  <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-semibold border border-slate-200">
    <span className="w-3 h-3 rounded-sm inline-block border border-slate-300" style={{ backgroundColor: color }} />
    {band || '?'}
  </span>
);

const PerfGroup = ({ title, entries, chipCls, testid }) => {
  if (!entries?.length) return null;
  return (
    <div className="mt-3" data-testid={testid}>
      <p className="text-sm font-semibold text-slate-700 mb-1">{title} ({entries.length})</p>
      <div className="space-y-1.5">
        {entries.map((e, i) => (
          <div key={i} className="border border-slate-200 rounded-md px-3 py-1.5 flex items-center gap-2 flex-wrap text-sm">
            <span className={`text-xs font-bold rounded-full px-2 py-0.5 ${chipCls}`}>
              {e.pct > 0 ? `+${e.pct}%` : (e.pct < 0 ? `${e.pct}%` : '=')}
            </span>
            <span className="font-semibold">{e.code}</span>
            <span className="text-slate-500">{e.name}</span>
            <span className="text-xs text-slate-400">{e.weeks} settimane · {e.nights} notti</span>
            <span className="text-xs text-slate-500 ml-auto">
              {(e.bands || []).map(b => {
                const oldV = b.old_owner != null ? b.old_owner : b.old_daily;
                const newV = b.old_owner != null ? b.new_owner : b.new_daily;
                return `${b.band}: ${oldV != null ? oldV : '—'}→${newV != null ? newV : '—'} €`;
              }).join(' · ')}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

export default function PricelistCalendarsPage() {
  const [calendars, setCalendars] = useState([]);
  const [names, setNames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [calName, setCalName] = useState('');
  const [newCalName, setNewCalName] = useState('');
  const effectiveCalName = calName === '__new__' ? newCalName.trim() : calName;
  const [year, setYear] = useState(new Date().getFullYear() + 1);
  const [file, setFile] = useState(null);
  const [parsing, setParsing] = useState(false);
  const [preview, setPreview] = useState(null);
  const [bandMap, setBandMap] = useState({});
  const [confirming, setConfirming] = useState(false);
  const [auditing, setAuditing] = useState(false);

  const runAudit = async () => {
    setAuditing(true);
    try {
      const res = await api.post('/pricelist-calendars/audit');
      const r = res.data;
      toast.info(`Audit ${r.year}: ${r.missing_calendars?.length || 0} calendari mancanti · ${r.personalizzati?.length || 0} personalizzati · ${r.mismatches?.length || 0} listini non conformi${r.email_sent ? ' — email inviata' : ''}`, { duration: 12000 });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore audit');
    } finally {
      setAuditing(false);
    }
  };
  const [shiftYear, setShiftYear] = useState(new Date().getFullYear() + 1);
  const [shiftOverwrite, setShiftOverwrite] = useState(false);
  const [shifting, setShifting] = useState(false);
  const [shiftReport, setShiftReport] = useState(null);

  const [genYear, setGenYear] = useState(new Date().getFullYear() + 1);
  const [genOverwrite, setGenOverwrite] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [genReport, setGenReport] = useState(null);

  const generateYear = async () => {
    if (!window.confirm(`Generare i listini ${genYear} per TUTTI gli immobili attivi con calendario? Prezzi = BASE ${genYear - 1}, senza aggiustamenti (la regola ±5% si applica il 1° gennaio ${genYear}).`)) return;
    setGenerating(true);
    setGenReport(null);
    try {
      const res = await api.post('/pricelist-calendars/generate-year', { target_year: genYear, overwrite: genOverwrite });
      setGenReport(res.data);
      toast.success(`Listini ${genYear}: generati ${res.data.totals.done} immobili · saltati ${res.data.totals.skipped} · ${res.data.totals.periods_created} periodi`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore generazione', { duration: 8000 });
    } finally {
      setGenerating(false);
    }
  };

  const [perfYear, setPerfYear] = useState(new Date().getFullYear() + 1);
  const [perfRunning, setPerfRunning] = useState(false);
  const [perfReport, setPerfReport] = useState(null);

  const runPerformance = async (dryRun) => {
    if (!dryRun && !window.confirm(`Applicare DAVVERO l'aggiustamento ±5% ai listini ${perfYear} (vendite ${perfYear - 1}) e inviare le email recap ad amministrazione, proprietari e partner?`)) return;
    setPerfRunning(true);
    try {
      const res = await api.post('/pricelist-calendars/apply-performance', { year: perfYear, dry_run: dryRun });
      setPerfReport(res.data);
      toast.success(`${dryRun ? 'Anteprima' : 'Aggiustamento APPLICATO'}: ${res.data.increased.length} aumentati · ${res.data.decreased.length} diminuiti · ${res.data.unchanged.length} invariati`, { duration: 10000 });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore aggiustamento', { duration: 8000 });
    } finally {
      setPerfRunning(false);
    }
  };

  const generatePersonalizzati = async () => {
    if (!window.confirm(`Generare i listini ${shiftYear + 1} per tutti gli immobili ATTIVI con calendario Personalizzato, partendo dal ${shiftYear}?`)) return;
    setShifting(true);
    setShiftReport(null);
    try {
      const res = await api.post('/pricelist-calendars/generate-personalizzati', { from_year: shiftYear, overwrite: shiftOverwrite });
      setShiftReport(res.data);
      const done = res.data.properties.filter(p => p.created > 0).length;
      toast.success(`Generazione completata: ${done} immobili su ${res.data.properties.length}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore generazione', { duration: 8000 });
    } finally {
      setShifting(false);
    }
  };

  const load = useCallback(async () => {
    try {
      const [calsRes, namesRes] = await Promise.all([
        api.get('/pricelist-calendars'),
        api.get('/pricelist-calendars/meta/names'),
      ]);
      setCalendars(calsRes.data.calendars || []);
      setNames((namesRes.data.names || []).filter(n => n.name !== 'Personalizzato'));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore caricamento calendari');
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const analyze = async () => {
    if (!effectiveCalName) return toast.error('Scegli il calendario');
    if (!file) return toast.error('Carica il PDF del calendario');
    setParsing(true);
    setPreview(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('calendar_name', effectiveCalName);
      fd.append('year', year);
      const res = await api.post('/pricelist-calendars/parse', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setPreview(res.data);
      const m = {};
      (res.data.colors || []).forEach(c => { m[c.color] = c.band_suggested || ''; });
      setBandMap(m);
      if ((res.data.warnings || []).length === 0) toast.success(`PDF letto: ${res.data.periods.length} periodi, ${res.data.colors.length} fasce`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore lettura PDF', { duration: 10000 });
    } finally {
      setParsing(false);
    }
  };

  const confirm = async () => {
    setConfirming(true);
    try {
      await api.post(`/pricelist-calendars/${preview.draft_id}/confirm`, { color_bands: bandMap });
      toast.success(`Calendario "${effectiveCalName}" ${year} salvato`);
      setPreview(null); setFile(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore salvataggio', { duration: 10000 });
    } finally {
      setConfirming(false);
    }
  };

  const remove = async (cal) => {
    if (!window.confirm(`Eliminare il calendario "${cal.calendar_name}" ${cal.year}?`)) return;
    try {
      await api.delete(`/pricelist-calendars/${cal.id}`);
      toast.success('Calendario eliminato');
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore eliminazione');
    }
  };

  return (
    <Layout title="Calendari Listini">
      <div className="space-y-6 fade-in" data-testid="pricelist-calendars-page">
        <Card className="p-6 border-0 shadow-lg">
          <div className="flex items-center gap-3 mb-1">
            <CalendarRange className="h-6 w-6 text-emerald-600" />
            <h2 className="text-xl font-bold text-slate-900">Calendari Listini</h2>
          </div>
          <p className="text-sm text-slate-500 mb-5">
            Carica il PDF del calendario fasce di un anno: il sistema legge i colori, ti mostra i periodi estratti e,
            una volta confermato, il calendario sarà la base per la generazione automatica dei listini.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
            <div>
              <label className="text-xs font-medium text-slate-600 block mb-1">Calendario</label>
              <select value={calName} onChange={e => setCalName(e.target.value)}
                className="w-full h-10 rounded-md border border-slate-200 px-3 text-sm bg-white"
                data-testid="calendar-name-select">
                <option value="">— scegli —</option>
                {names.map(n => (
                  <option key={n.name} value={n.name}>{n.name} ({n.properties} immobili)</option>
                ))}
                <option value="__new__">✏️ Nuovo calendario…</option>
              </select>
              {calName === '__new__' && (
                <Input className="mt-2" placeholder="Nome nuovo calendario" value={newCalName}
                  onChange={e => setNewCalName(e.target.value)} data-testid="calendar-new-name-input" />
              )}
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600 block mb-1">Anno</label>
              <Input type="number" value={year} onChange={e => setYear(parseInt(e.target.value) || year)}
                min={2025} max={2100} data-testid="calendar-year-input" />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600 block mb-1">PDF calendario</label>
              <Input type="file" accept="application/pdf" onChange={e => setFile(e.target.files?.[0] || null)}
                data-testid="calendar-pdf-input" />
            </div>
            <Button onClick={analyze} disabled={parsing} className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="calendar-analyze-btn">
              {parsing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Upload className="h-4 w-4 mr-2" />}
              Analizza PDF
            </Button>
            <Button variant="outline" onClick={runAudit} disabled={auditing}
              className="border-slate-300 text-slate-700" data-testid="calendar-audit-btn"
              title="Controlla incongruenze tra immobili, listini e calendari e invia l'email di report">
              {auditing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <AlertTriangle className="h-4 w-4 mr-2" />}
              Verifica congruenza
            </Button>
          </div>
        </Card>

        {preview && (
          <Card className="p-6 border border-emerald-200 shadow-lg" data-testid="calendar-preview">
            <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
              <div>
                <h3 className="font-bold text-slate-900">Anteprima — {effectiveCalName} {preview.year}</h3>
                <p className="text-xs text-slate-500">
                  Giorni coperti: {preview.coverage.covered}/{preview.coverage.total} · {preview.periods.length} periodi · {preview.colors.length} fasce
                </p>
              </div>
              <Button onClick={confirm} disabled={confirming || Object.values(bandMap).some(v => !v)}
                className="bg-emerald-600 hover:bg-emerald-700" data-testid="calendar-confirm-btn">
                {confirming ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <CheckCircle className="h-4 w-4 mr-2" />}
                Conferma e salva
              </Button>
            </div>
            {(preview.warnings || []).map((w, i) => (
              <div key={i} className="flex items-center gap-2 text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2 mb-3">
                <AlertTriangle className="h-4 w-4 shrink-0" /> {w}
              </div>
            ))}
            <div className="flex flex-wrap gap-4 mb-4">
              {preview.colors.map(c => (
                <div key={c.color} className="flex items-center gap-2 border border-slate-200 rounded-md px-3 py-2">
                  <span className="w-5 h-5 rounded-sm border border-slate-300" style={{ backgroundColor: c.color }} />
                  <span className="text-xs text-slate-500">{c.days} giorni →</span>
                  <select value={bandMap[c.color] || ''} onChange={e => setBandMap(m => ({ ...m, [c.color]: e.target.value }))}
                    className="h-8 rounded border border-slate-200 px-2 text-sm bg-white"
                    data-testid={`band-select-${c.color.replace('#', '')}`}>
                    <option value="">Fascia?</option>
                    {BAND_OPTIONS.map(b => <option key={b} value={b}>Fascia {b}</option>)}
                  </select>
                </div>
              ))}
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="calendar-periods-table">
                <thead><tr className="text-left text-xs text-slate-500 border-b">
                  <th className="py-2 pr-4">#</th><th className="py-2 pr-4">Arrivo</th>
                  <th className="py-2 pr-4">Partenza</th><th className="py-2 pr-4">Notti</th><th className="py-2">Fascia</th>
                </tr></thead>
                <tbody>
                  {preview.periods.map((p, i) => (
                    <tr key={i} className="border-b border-slate-100">
                      <td className="py-1.5 pr-4 text-slate-400">{i + 1}</td>
                      <td className="py-1.5 pr-4 font-medium">{fmtDate(p.arrival)}</td>
                      <td className="py-1.5 pr-4 font-medium">{fmtDate(p.departure)}</td>
                      <td className="py-1.5 pr-4 text-slate-500">
                        {Math.round((new Date(p.departure) - new Date(p.arrival)) / 86400000)}
                      </td>
                      <td className="py-1.5"><BandChip color={p.color} band={bandMap[p.color]} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        )}

        <Card className="p-6 border-0 shadow-lg" data-testid="personalizzati-card">
          <h3 className="font-bold text-slate-900 mb-1">Listini "Personalizzato" — slittamento automatico</h3>
          <p className="text-sm text-slate-500 mb-4">
            Per gli immobili con calendario Personalizzato: rigenera i listini dell'anno successivo uguali
            all'anno scelto, adattando le date (stesso giorno della settimana, Pasqua e festività ricalcolate).
            I prezzi vengono copiati identici e ogni periodo resta modificabile dalla scheda immobile.
          </p>
          <div className="flex items-end gap-4 flex-wrap">
            <div>
              <label className="text-xs font-medium text-slate-600 block mb-1">Anno di partenza</label>
              <Input type="number" value={shiftYear} onChange={e => setShiftYear(parseInt(e.target.value) || shiftYear)}
                min={2020} max={2099} className="w-28" data-testid="shift-from-year-input" />
            </div>
            <label className="flex items-center gap-2 text-sm text-slate-600 pb-2.5">
              <input type="checkbox" checked={shiftOverwrite} onChange={e => setShiftOverwrite(e.target.checked)}
                data-testid="shift-overwrite-check" />
              Sovrascrivi i periodi già generati per il {shiftYear + 1}
            </label>
            <Button onClick={generatePersonalizzati} disabled={shifting}
              className="bg-blue-600 hover:bg-blue-700" data-testid="shift-generate-btn">
              {shifting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <CalendarRange className="h-4 w-4 mr-2" />}
              Genera listini {shiftYear + 1} dal {shiftYear}
            </Button>
          </div>
          {shiftReport && (
            <div className="mt-4 space-y-3" data-testid="shift-report">
              {shiftReport.properties.map((r, i) => (
                <div key={i} className="border border-slate-200 rounded-lg p-3">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-sm">{r.code}</span>
                    <span className="text-sm text-slate-500">{r.name}</span>
                    {r.skipped
                      ? <span className="text-xs bg-amber-50 text-amber-700 border border-amber-200 rounded-full px-2 py-0.5">{r.skipped}</span>
                      : <span className="text-xs bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-full px-2 py-0.5">{r.created} periodi generati</span>}
                  </div>
                  {(r.warnings || []).map((w, j) => (
                    <p key={j} className="text-xs text-amber-700 mt-1 flex items-center gap-1"><AlertTriangle className="h-3 w-3" /> {w}</p>
                  ))}
                  {(r.periods || []).length > 0 && (
                    <table className="w-full text-xs mt-2">
                      <thead><tr className="text-left text-slate-400 border-b">
                        <th className="py-1 pr-3">Arrivo</th><th className="py-1 pr-3">Partenza</th>
                        <th className="py-1 pr-3">Fascia</th><th className="py-1 pr-3">Prezzo</th><th className="py-1">Ancoraggio</th>
                      </tr></thead>
                      <tbody>
                        {r.periods.map((p, j) => (
                          <tr key={j} className="border-b border-slate-50">
                            <td className="py-0.5 pr-3 font-medium">{fmtDate(p.arrival)}</td>
                            <td className="py-0.5 pr-3 font-medium">{fmtDate(p.departure)}</td>
                            <td className="py-0.5 pr-3">{p.band || '—'}</td>
                            <td className="py-0.5 pr-3">{p.daily_price != null ? `${p.daily_price} €` : '—'}</td>
                            <td className="py-0.5 text-slate-500">{p.anchor}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              ))}
            </div>
          )}
        </Card>

        <Card className="p-6 border-0 shadow-lg" data-testid="generate-year-card">
          <h3 className="font-bold text-slate-900 mb-1">Generazione listini anno successivo</h3>
          <p className="text-sm text-slate-500 mb-4">
            Genera i listini di un anno per tutti gli immobili attivi: prende i prezzi <b>BASE</b> per fascia
            dell'anno precedente (le modifiche/sconti successivi non contano) e li mappa sulle date del calendario
            salvato dell'anno nuovo. I "Personalizzati" usano lo slittamento automatico. Nessun aggiustamento in
            questa fase: la regola ±5% si applica da sola il 1° gennaio. <b>Automatico ogni anno il 2 maggio.</b>
          </p>
          <div className="flex items-end gap-4 flex-wrap">
            <div>
              <label className="text-xs font-medium text-slate-600 block mb-1">Anno da generare</label>
              <Input type="number" value={genYear} onChange={e => setGenYear(parseInt(e.target.value) || genYear)}
                min={2025} max={2099} className="w-28" data-testid="gen-year-input" />
            </div>
            <label className="flex items-center gap-2 text-sm text-slate-600 pb-2.5">
              <input type="checkbox" checked={genOverwrite} onChange={e => setGenOverwrite(e.target.checked)}
                data-testid="gen-overwrite-check" />
              Sovrascrivi i periodi già generati per il {genYear}
            </label>
            <Button onClick={generateYear} disabled={generating}
              className="bg-emerald-600 hover:bg-emerald-700" data-testid="gen-run-btn">
              {generating ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <CalendarRange className="h-4 w-4 mr-2" />}
              Genera listini {genYear} dal {genYear - 1}
            </Button>
          </div>
          {genReport && (
            <div className="mt-4" data-testid="gen-report">
              <p className="text-sm font-semibold text-slate-700 mb-2">
                Generati {genReport.totals.done} · Saltati {genReport.totals.skipped} · {genReport.totals.periods_created} periodi
              </p>
              <div className="space-y-1.5 max-h-96 overflow-y-auto pr-1">
                {genReport.properties.map((r, i) => (
                  <div key={i} className="border border-slate-200 rounded-md px-3 py-1.5 text-sm">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold">{r.code}</span>
                      <span className="text-slate-500">{r.name}</span>
                      <span className="text-xs text-slate-400">{r.calendar}</span>
                      {r.skipped
                        ? <span className="text-xs bg-amber-50 text-amber-700 border border-amber-200 rounded-full px-2 py-0.5">{r.skipped}</span>
                        : <span className="text-xs bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-full px-2 py-0.5">{r.created} periodi</span>}
                    </div>
                    {(r.warnings || []).map((w, j) => (
                      <p key={j} className="text-xs text-amber-700 mt-0.5 flex items-center gap-1"><AlertTriangle className="h-3 w-3" /> {w}</p>
                    ))}
                  </div>
                ))}
              </div>
            </div>
          )}
        </Card>

        <Card className="p-6 border-0 shadow-lg" data-testid="performance-card">
          <h3 className="font-bold text-slate-900 mb-1">Aggiustamento performance ±5% (1° gennaio)</h3>
          <p className="text-sm text-slate-500 mb-4">
            Regola contrattuale: settimane vendute nell'anno concluso = notti (prenotazioni confermate/completate,
            chiusure proprietario escluse) ÷ 7. Più di 20 settimane → prezzo proprietario <b>+5%</b> (arrotondato
            per difetto); meno di 10 → <b>−5%</b> (per eccesso); tra 10 e 20 → invariato. Markup identico.
            Si applica SOLO ai listini generati dal sistema. <b>Automatico ogni 1° gennaio</b> con email recap ad
            amministrazione, proprietari e partner.
          </p>
          <div className="flex items-end gap-4 flex-wrap">
            <div>
              <label className="text-xs font-medium text-slate-600 block mb-1">Anno listini da aggiustare</label>
              <Input type="number" value={perfYear} onChange={e => setPerfYear(parseInt(e.target.value) || perfYear)}
                min={2025} max={2099} className="w-28" data-testid="perf-year-input" />
            </div>
            <Button variant="outline" onClick={() => runPerformance(true)} disabled={perfRunning}
              className="border-slate-300 text-slate-700" data-testid="perf-preview-btn">
              {perfRunning ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <FileText className="h-4 w-4 mr-2" />}
              Anteprima (vendite {perfYear - 1})
            </Button>
            <Button onClick={() => runPerformance(false)} disabled={perfRunning}
              className="bg-rose-600 hover:bg-rose-700" data-testid="perf-apply-btn">
              {perfRunning ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <CheckCircle className="h-4 w-4 mr-2" />}
              Applica e invia email
            </Button>
          </div>
          {perfReport && (
            <div className="mt-4" data-testid="perf-report">
              <p className="text-xs text-slate-500 mb-1">
                {perfReport.dry_run ? 'ANTEPRIMA — nessuna modifica salvata' : 'APPLICATO — prezzi aggiornati'} · vendite di riferimento {perfReport.ref_year}
                {perfReport.emails?.blocked_preview && ' · email bloccate (ambiente di test)'}
              </p>
              <PerfGroup title="Aumentati +5%" entries={perfReport.increased} testid="perf-increased"
                chipCls="bg-emerald-100 text-emerald-700" />
              <PerfGroup title="Diminuiti −5%" entries={perfReport.decreased} testid="perf-decreased"
                chipCls="bg-rose-100 text-rose-700" />
              <PerfGroup title="Invariati" entries={perfReport.unchanged} testid="perf-unchanged"
                chipCls="bg-slate-100 text-slate-600" />
              {(perfReport.skipped || []).length > 0 && (
                <div className="mt-3">
                  <p className="text-sm font-semibold text-slate-700 mb-1">Saltati ({perfReport.skipped.length})</p>
                  {perfReport.skipped.map((s, i) => (
                    <p key={i} className="text-xs text-amber-700">{s.code} {s.name} — {s.reason}</p>
                  ))}
                </div>
              )}
              {perfReport.increased.length + perfReport.decreased.length + perfReport.unchanged.length === 0 && (
                <p className="text-sm text-slate-500 mt-2" data-testid="perf-empty-msg">
                  Nessun immobile con listini {perfReport.year} generati dal sistema: genera prima i listini con la card qui sopra.
                </p>
              )}
            </div>
          )}
        </Card>

        <Card className="p-6 border-0 shadow-lg">
          <h3 className="font-bold text-slate-900 mb-4">Calendari salvati</h3>
          {loading ? (
            <div className="text-center py-8 text-slate-400"><Loader2 className="h-5 w-5 animate-spin mx-auto" /></div>
          ) : calendars.length === 0 ? (
            <p className="text-sm text-slate-500" data-testid="no-calendars-msg">
              Nessun calendario salvato. I listini fino al 2027 sono già stati importati dal vecchio sito:
              da qui in poi caricherai i calendari degli anni successivi (es. 2028).
            </p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {calendars.map(cal => (
                <div key={cal.id} className="border border-slate-200 rounded-lg p-4" data-testid={`calendar-card-${cal.calendar_name.replace(/\s/g, '-')}-${cal.year}`}>
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="font-semibold text-slate-900">{cal.calendar_name} <span className="text-emerald-700">{cal.year}</span></p>
                      <p className="text-xs text-slate-500 mt-0.5">
                        {(cal.periods || []).length} periodi · fasce {[...new Set(Object.values(cal.color_bands || {}))].sort().join(', ')}
                      </p>
                      <p className="text-xs text-slate-400 mt-1 flex items-center gap-1">
                        <FileText className="h-3 w-3" /> {cal.source_filename || 'PDF'} · {cal.confirmed_by || cal.created_by}
                      </p>
                    </div>
                    <Button variant="ghost" size="sm" onClick={() => remove(cal)}
                      className="text-red-500 hover:bg-red-50" data-testid={`calendar-delete-${cal.year}`}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                  <div className="flex h-3 w-full rounded-full overflow-hidden mt-3 border border-slate-200">
                    {(cal.periods || []).map((p, i) => {
                      const days = Math.round((new Date(p.departure) - new Date(p.arrival)) / 86400000);
                      return <span key={i} title={`${fmtDate(p.arrival)} → ${fmtDate(p.departure)} (${p.band})`}
                        style={{ width: `${(days / 365) * 100}%`, backgroundColor: p.color }} />;
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>
    </Layout>
  );
}
